import { Injectable, inject } from '@angular/core';
import { UIRouter } from '@uirouter/core';
import { lastValueFrom } from 'rxjs';
import { handle } from 'src/shared-utilities/async-helpers/promise-helpers';

import { SlidesStateNames } from '../../../router/slides-states';
import { MenuService } from '../../../slides/menu/menu.service';
import { ClientJourneyEngagement } from '../../models/deal-resources/client-journey-engagement';
import { ClientJourneyEngagementPage } from '../../models/deal-resources/client-journey-engagement-page';
import { ResourceContextStore } from '../resource-context-store';
import { ResourceContext } from '../resource-context.service';
import { ClientJourneyContext } from './client-journey-context';

@Injectable({
  providedIn: 'root',
})
export class ClientJourneyEngagementService {
  private uiRouter = inject(UIRouter);
  private menuService = inject(MenuService);
  private resourceContextStore = inject(ResourceContextStore);

  // We can't inject the ClientJourneyContext since that'll cause
  // a circular reference on injection. However, it's possible
  // to pass the instance from the context into this service when
  // the tracking is started.
  private clientJourneyContext: ClientJourneyContext;

  private deregisterTransitionHook: Function;
  private progressContext: ResourceContext<ClientJourneyEngagement>;
  async startTracking(clientJourneyContext: ClientJourneyContext): Promise<void> {
    await this.stopTracking(); // Ensure we're stopped before starting
    this.clientJourneyContext = clientJourneyContext;

    const primaryBorrower = this.clientJourneyContext.deal.getPrimaryBorrower();

    const engagement = primaryBorrower.cjEngagement;
    if (!engagement) return;

    this.progressContext = this.resourceContextStore.getByResource(engagement, true);
    this.deregisterTransitionHook = this.uiRouter.transitionService.onSuccess(
      { to: 'client.journey.slides.**' },
      () => {
        this.savePage();
      },
    );

    setTimeout(() => this.savePage());
  }

  async stopTracking(): Promise<void> {
    if (this.progressContext) {
      await this.progressContext.flush();
      this.resourceContextStore.removeByKey(this.progressContext.key);
      this.progressContext = null;
    }

    if (this.deregisterTransitionHook) {
      this.deregisterTransitionHook();
      this.deregisterTransitionHook = null;
    }

    this.clientJourneyContext = null;
  }

  async savePage() {
    if (!this.clientJourneyContext) return;

    const primaryBorrower = this.clientJourneyContext.deal.getPrimaryBorrower();
    const cjEngagement = primaryBorrower.cjEngagement;

    const params = this.uiRouter.globals?.params || {};
    const response$ = cjEngagement.postRelation('cjEngagementPages', {
      body: {
        url: location.href.toString(),
        stateName: this.uiRouter.globals.current.name,
        stateParams: JSON.stringify(params),
      },
    });

    const [savedPage, err] = await handle(lastValueFrom(response$));
    if (err || !savedPage) {
      this.updateMenuItems();
      return;
    }

    // Add or update the returned page in the pages array
    cjEngagement.pages = cjEngagement.pages || [];
    const pages = cjEngagement.pages;
    const pageIndex = pages.findIndex(p => p.clientJourneyEngagementPageId == savedPage.clientJourneyEngagementPageId);
    if (pageIndex != -1) {
      pages.splice(pageIndex, 1, savedPage);
    } else {
      pages.push(savedPage);
    }

    this.updateMenuItems();
  }

  private getPrimaryBorrowerTrackedStateName() {
    const primaryBorrower = this.clientJourneyContext.deal.getPrimaryBorrower();
    const engagementPages = primaryBorrower.cjEngagement.pages;
    return this.getTrackedStateName(engagementPages);
  }

  private getTrackedStateName(engagementPages: Array<ClientJourneyEngagementPage>): string {
    if (!engagementPages) return '';

    let maxSlideIndex = -1;
    for (const engagementPage of engagementPages) {
      const engagementPageSlideIndex = this.getSlideIndex(engagementPage.stateName);
      maxSlideIndex = Math.max(maxSlideIndex, engagementPageSlideIndex);
    }

    const menuSlideStates = this.menuService.getSlideSates();
    const trackedState = menuSlideStates[maxSlideIndex] || {};
    const trackedStateName = trackedState.name || '';
    return trackedStateName;
  }

  getSlideIndex(stateName): number {
    if (!stateName) return -1;

    const menuSlideStates = this.menuService.getSlideSates();
    const stateIndex = menuSlideStates.findIndex(s => stateName.startsWith(s.name));

    return stateIndex;
  }

  getMaxReachedSlideIndex(): number {
    const currentState = this.uiRouter.globals.current;
    const currentStateName = currentState.name;
    const trackedStateName = this.getPrimaryBorrowerTrackedStateName() || currentStateName;

    const currentSlideIndex = this.getSlideIndex(currentStateName);
    const trackedSlideIndex = this.getSlideIndex(trackedStateName);

    const maxReachedSlideIndex = Math.max(currentSlideIndex, trackedSlideIndex);

    return maxReachedSlideIndex;
  }

  /**
   * This will check the exact state, not just a slide. A slide can
   * include multiple states.
   */
  hasReachedSate(stateName: string): boolean {
    const currentState = this.uiRouter.globals.current;
    const currentStateName = currentState.name;
    if (currentStateName == stateName) return true;

    const primaryBorrower = this.clientJourneyContext.deal.getPrimaryBorrower();
    const engagementPages = primaryBorrower.cjEngagement.pages;

    const hasReachedState = engagementPages.some(p => p.stateName == stateName);
    return hasReachedState;
  }

  private updateMenuItems(): void {
    const currentState = this.uiRouter.globals.current;
    const currentStateName = currentState.name;

    // Menu items are enabled up to a cut-off and then they're disabled.
    const menuSlideStates = this.menuService.getSlideSates();
    const maxReachedSlideIndex = this.getMaxReachedSlideIndex();

    const hasReachedBorrowerSummary = this.hasReachedSate(SlidesStateNames.BorrowersSummary);

    menuSlideStates.forEach((slideState, index) => {
      let menuItemEnabled = index <= maxReachedSlideIndex;
      menuItemEnabled ||= hasReachedBorrowerSummary;

      slideState.data.menuItemEnabled = menuItemEnabled;

      const hasReached = index <= maxReachedSlideIndex;
      slideState.data.hasReached = hasReached;
    });
  }

  /**
   * Calculates the percentage completed
   * @param maxPercentage We default to a max of 95% with the remaining 5
   * to be assumed when the deal is no longer in the lead status (has been
   * submitted)
   */
  getProgressPercentage(engagementPages: Array<ClientJourneyEngagementPage>, maxPercentage = 95): number {
    const trackedStateName = this.getTrackedStateName(engagementPages);
    const trackedSlideIndex = this.getSlideIndex(trackedStateName);

    const menuSlideStates = this.menuService.getSlideSates();
    const percentagePerMenuSlides = maxPercentage / menuSlideStates.length;

    const slideNumber = trackedSlideIndex + 1;
    const progressPercentage = Math.round(percentagePerMenuSlides * slideNumber) || 0;

    return progressPercentage;
  }
}
